import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { euro } from "@/lib/format";
import { statusInfo, daysWaiting } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { FilePlus2, FileText, ArrowRight, AlertCircle, Trash2, Megaphone, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SalesDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [o, a] = await Promise.all([api.get("/offers"), api.get("/announcements")]);
      setOffers(o.data);
      setAnnouncements(a.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminare questa offerta?")) return;
    await api.delete(`/offers/${id}`);
    toast.success("Offerta eliminata");
    setOffers((o) => o.filter((x) => x.id !== id));
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-navy tracking-tight">Le tue offerte</h1>
          <p className="text-muted-foreground mt-1">Ciao {user?.name || "commerciale"}, gestisci i preventivi per i tuoi clienti.</p>
        </div>
        <Button onClick={() => navigate("/configuratore")} data-testid="new-offer-button" className="h-11 font-bold gap-2">
          <FilePlus2 size={18} /> Nuova Offerta
        </Button>
      </div>

      {!user?.agency_logo_url && (
        <Link to="/profilo" className="flex items-center gap-3 bg-accent/10 border border-accent/30 text-navy rounded-xl p-4 mb-6 hover:bg-accent/15 transition-colors duration-200">
          <AlertCircle className="text-accent shrink-0" size={20} />
          <span className="text-sm font-semibold">Carica il logo della tua agenzia per personalizzare le offerte →</span>
        </Link>
      )}

      {announcements.length > 0 && (
        <div className="mb-8" data-testid="announcements-section">
          <h2 className="font-extrabold text-navy flex items-center gap-2 mb-3"><Megaphone size={18} className="text-primary" /> Comunicazioni</h2>
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} data-testid={`sales-announcement-${a.id}`} className="bg-white border border-border rounded-xl p-4 flex gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.type === "feature" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                  {a.type === "feature" ? <Sparkles size={18} /> : <Megaphone size={18} />}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-navy">{a.title} <span className="text-xs text-muted-foreground font-normal">· {(a.created_at || "").slice(0, 10)}</span></p>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{a.body}</p>
                  {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="text-sm text-accent font-semibold inline-flex items-center gap-1 mt-1 hover:underline"><ExternalLink size={13} /> Scopri di più</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Caricamento...</p>
      ) : offers.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-16 text-center bg-white">
          <FileText className="mx-auto text-muted-foreground mb-3" size={40} />
          <p className="text-navy font-bold text-lg">Nessuna offerta ancora</p>
          <p className="text-muted-foreground text-sm mt-1 mb-5">Crea la tua prima offerta con il configuratore.</p>
          <Button onClick={() => navigate("/configuratore")} className="font-bold gap-2">
            <FilePlus2 size={18} /> Crea offerta
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {offers.map((o) => (
            <Link
              key={o.id}
              to={`/offerte/${o.id}`}
              data-testid={`offer-card-${o.id}`}
              className="group bg-white border border-border rounded-xl p-5 hover:-translate-y-1 hover:border-primary transition-transform duration-200 flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-extrabold text-navy truncate text-lg">{o.client_name}</p>
                  {o.client_company && <p className="text-sm text-muted-foreground truncate">{o.client_company}</p>}
                </div>
                <button onClick={(e) => remove(o.id, e)} className="text-muted-foreground hover:text-destructive transition-colors duration-200 shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
              <span className={`inline-block mt-3 px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusInfo(o.status).cls}`}>
                {statusInfo(o.status).label}
              </span>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="text-2xl font-extrabold text-navy">{euro(o.totals?.monthly_final != null ? o.totals.monthly_final : o.totals?.monthly_net)}<span className="text-sm text-muted-foreground font-semibold">/mese</span></p>
                  {o.totals?.oneoff_net > 0 && <p className="text-xs text-muted-foreground">+ {euro(o.totals.oneoff_net)} una tantum</p>}
                </div>
                <ArrowRight className="text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-200" size={20} />
              </div>
              <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
                <span>{(o.created_at || "").slice(0, 10)}</span>
                <span>{o.status === "won" ? `Convertita in ${daysWaiting(o)}g` : `${daysWaiting(o)}g in attesa`}</span>
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
